import React, { useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { translate } from '../services/translate';
import Toast from 'react-native-toast-message';
import { ClientError, NotFound } from '../services/central-api/base-service';
import { centralAPI } from '../services/central-api';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../components/Container';
import Card from '../components/Card';
import CustomButton from '../components/CustomButton';
import { RootScreensParamLists } from '../typings/Params';
import { useRoute, RouteProp } from '@react-navigation/native';
import { forceRedirectToLogin } from '../services/navigation-assistant';
import SentinelXLogo from '../components/SentinelXLogo';
import PasswordForm from '../components/PasswordForm';
import { getPasswordRequirements } from '../services/password-requirements';

export default function ResetPassword() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<RootScreensParamLists, 'ResetPassword'>>();

    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const isFormValid =
        password !== '' &&
        confirmPassword === password &&
        getPasswordRequirements(password).isValid;

    async function resetPassword() {
        try {
            setLoading(true);
            await centralAPI.resetPassword(
                password,
                confirmPassword,
                route.params.token,
            );
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            forceRedirectToLogin();

        } catch (err) {
            if (err instanceof NotFound) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('TokenNotFound'),
                });
            }

            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Card>
                <View style={styles.imageView}>
                    <SentinelXLogo />
                </View>
                <View style={styles.formContainer}>
                    <PasswordForm
                        showOldPassword={false}
                        stacked={true}
                        password={password}
                        confirmPassword={confirmPassword}
                        onChangePassword={setPassword}
                        onChangeConfirmPassword={setConfirmPassword}
                    />

                    {loading ?
                        <View style={{ height: 40 }}>
                            <ActivityIndicator size='small' color={theme.colors.iconColor} />
                        </View>
                        : <CustomButton
                            onPress={() => resetPassword()}
                            text={translate('save')}
                            type='primary'
                            disabled={!isFormValid}
                        />
                    }
                </View>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerText: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold'
    },
    label: {
        color: theme.colors.labelColor,
    },
    valid: {
        color: theme.colors.successes['300'],
    },
    invalid: {
        color: theme.colors.errors['300'],
    },
    formContainer: {
        flexDirection: 'column',
        gap: 30,
        maxWidth: 375,
        width: '100%'
    },
    imageView: {
        width: 260,
        height: 40,
    },
}));
